/**
 * Translation Service for ChartMind
 *
 * Phrase caching and sentence batching stay here; REST LLM calls delegate to
 * @healthdesk unified translation service (services/translation).
 */

import { getLanguageName } from '@healthdesk/shared-services';
import {
  normalizeTranslationLLMOutput,
  translateViaChartmindTranslateQuery,
} from '../../../../../services/translation';
import { getGlossary } from './translationGlossary';
import { getPhraseMap } from './clinicalPhrases';

// Re-export for callers that imported from this module
export { normalizeTranslationLLMOutput };

// Normalize text for cache lookup (lowercase, trim, remove extra spaces)
const normalizeText = (text) => {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
};

// Check phrase cache (uses clinical phrases module)
const checkCache = (text, fromLang, toLang) => {
  const cache = getPhraseMap(fromLang, toLang);

  if (!cache || Object.keys(cache).length === 0) return null;

  if (cache[text]) return cache[text];

  const normalized = normalizeText(text);
  for (const [key, value] of Object.entries(cache)) {
    if (normalizeText(key) === normalized) {
      return value;
    }
  }

  return null;
};

// Split text into sentences for better caching
const splitIntoSentences = (text) => {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const translateWithOpenAI = async (text, fromLang, toLang) => {
  const fromLanguage = getLanguageName(fromLang) || fromLang;
  const toLanguage = getLanguageName(toLang) || toLang;

  try {
    let userInput =
      `From: ${fromLanguage}\nTo: ${toLanguage}\n\n` +
      'Rules: Translate ONLY the human-spoken text below. Do not add sentences, words, diagnoses, symptoms, or details that are not in that text. ' +
      'Do not complete partial phrases or infer what the speaker "meant" beyond faithful translation. ' +
      'If the text is fragmentary, translate the fragment as-is. Output nothing except the translation.\n\n' +
      `Text to translate:\n${text}`;
    const glossary = getGlossary(fromLang, toLang);
    if (glossary.length > 0) {
      const glossaryBlock = glossary.map((e) => `"${e.source}" → "${e.target}"`).join(', ');
      userInput += `\n\nUse these preferred terms when applicable: ${glossaryBlock}.`;
    }

    // promptId chartmind-translate = shared clinical-conversation MT (SCL-037), not ChartMind-only.
    return await translateViaChartmindTranslateQuery(userInput);
  } catch (error) {
    console.error('[translationService] Translation error:', error);
    throw error;
  }
};

/**
 * @param {string} text
 * @param {string} fromLang
 * @param {string} toLang
 * @returns {Promise<{translation: string, fromCache: boolean}>}
 */
export const translateText = async (text, fromLang, toLang) => {
  if (fromLang === toLang) {
    return { translation: text, fromCache: true };
  }

  if (!text || text.trim().length === 0) {
    return { translation: '', fromCache: true };
  }

  const sentences = splitIntoSentences(text);
  const translations = [];
  let allFromCache = true;

  for (const sentence of sentences) {
    const cached = checkCache(sentence, fromLang, toLang);

    if (cached) {
      translations.push(cached);
    } else {
      allFromCache = false;
      const translated = await translateWithOpenAI(sentence, fromLang, toLang);
      translations.push(translated);
    }
  }

  return {
    translation: translations.join(' '),
    fromCache: allFromCache,
  };
};

/**
 * @param {string[]} texts
 * @param {string} fromLang
 * @param {string} toLang
 * @returns {Promise<string[]>}
 */
export const batchTranslate = async (texts, fromLang, toLang) => {
  if (fromLang === toLang) {
    return texts;
  }

  const results = new Array(texts.length);
  const uncachedIndices = [];
  const uncachedTexts = [];

  for (let i = 0; i < texts.length; i++) {
    const cached = checkCache(texts[i], fromLang, toLang);
    if (cached) {
      results[i] = cached;
    } else {
      uncachedIndices.push(i);
      uncachedTexts.push(texts[i]);
    }
  }

  if (uncachedTexts.length > 0) {
    const combinedText = uncachedTexts.join('\n---\n');
    const { translation } = await translateText(combinedText, fromLang, toLang);
    const translatedParts = translation.split('\n---\n');

    for (let i = 0; i < uncachedIndices.length; i++) {
      results[uncachedIndices[i]] = translatedParts[i] || uncachedTexts[i];
    }
  }

  return results;
};

export const getSupportedLanguagePairs = () => [
  { from: 'en-US', to: 'sw-KE', label: 'English → Swahili' },
  { from: 'sw-KE', to: 'en-US', label: 'Swahili → English' },
  { from: 'en-US', to: 'es-ES', label: 'English → Spanish' },
  { from: 'es-ES', to: 'en-US', label: 'Spanish → English' },
  { from: 'en-US', to: 'fr-FR', label: 'English → French' },
  { from: 'fr-FR', to: 'en-US', label: 'French → English' },
  { from: 'sw-KE', to: 'es-ES', label: 'Swahili → Spanish' },
  { from: 'es-ES', to: 'sw-KE', label: 'Spanish → Swahili' },
  { from: 'sw-KE', to: 'fr-FR', label: 'Swahili → French' },
  { from: 'fr-FR', to: 'sw-KE', label: 'French → Swahili' },
  { from: 'es-ES', to: 'fr-FR', label: 'Spanish → French' },
  { from: 'fr-FR', to: 'es-ES', label: 'French → Spanish' },
];

export const isTranslationAvailable = (fromLang, toLang) => {
  if (fromLang === toLang) return false;

  const supportedPairs = getSupportedLanguagePairs();
  return supportedPairs.some((pair) => pair.from === fromLang && pair.to === toLang);
};

const translationService = {
  translateText,
  batchTranslate,
  getSupportedLanguagePairs,
  isTranslationAvailable,
};

export default translationService;
